import type { ReactNode } from 'react';
import {
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@roger/ui-components';
import { Loader2, MoreHorizontal, Pause, Play } from 'lucide-react';
import type {
  BespokeTemplateResponse,
  FormSchemaVersion,
  RogerDocFormTypeCatalogEntry,
} from '../../services/api/types.gen';
import type { RogerDocFormType } from '../../services/api/models/RogerDocFormType';
import { formatDateForDisplay } from '../../utils/dateUtils';
import { countBespokeBulkTargets } from './bulkTemplateStatus';
import { sortFormTypeCatalogByPrecedence } from './formPrecedence';
import {
  isEffective,
  pinFromFormSchemaVersion,
  type FormSchemaVersionPin,
} from './formSchemaUtils';

export function SchemaVersionBrowser({
  formTypeCatalog,
  catalogLoading,
  selectedRogerDocFormType,
  selectedCatalogLabel,
  onFormTypeChange,
  versions,
  versionsLoading,
  versionsError,
  selectedVersion,
  onVersionSelect,
  templates,
  pinsForCurrentFormType,
  onOpenBulk,
  actions,
}: {
  formTypeCatalog: RogerDocFormTypeCatalogEntry[];
  catalogLoading: boolean;
  selectedRogerDocFormType: RogerDocFormType | null;
  selectedCatalogLabel: string | undefined;
  onFormTypeChange: (formType: RogerDocFormType) => void;
  versions: FormSchemaVersion[];
  versionsLoading: boolean;
  versionsError: string | null;
  selectedVersion: FormSchemaVersion | null;
  onVersionSelect: (version: FormSchemaVersion) => void;
  templates: BespokeTemplateResponse[];
  pinsForCurrentFormType: FormSchemaVersionPin[];
  onOpenBulk: (
    direction: 'pause' | 'resume',
    scopeSummary: string,
    pinsFilter: FormSchemaVersionPin[] | null
  ) => void;
  /** Page-wide actions, shown at the right of the form type row. */
  actions?: ReactNode;
}) {
  return (
    <>
      <div className='flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between'>
        <div className='max-w-xs flex-1 min-w-[12rem]'>
          <label className='mb-1 block text-sm font-medium'>Form Type</label>
          <div className='flex items-center gap-2'>
            <div className='min-w-0 flex-1'>
              <Select
                value={selectedRogerDocFormType ?? ''}
                onValueChange={v => onFormTypeChange(v as RogerDocFormType)}
                disabled={catalogLoading || formTypeCatalog.length === 0}
              >
                <SelectTrigger>
                  <SelectValue
                    placeholder={
                      catalogLoading ? 'Loading form types…' : 'No form types'
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {sortFormTypeCatalogByPrecedence(formTypeCatalog).map(row => (
                    <SelectItem key={row.form_type} value={row.form_type}>
                      {row.display_label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <DropdownMenu modal={false}>
              <DropdownMenuTrigger asChild>
                <Button
                  type='button'
                  variant='outline'
                  className='h-8 w-8 shrink-0 p-0 [&_svg]:h-4 [&_svg]:w-4'
                  disabled={
                    catalogLoading ||
                    !selectedRogerDocFormType ||
                    pinsForCurrentFormType.length === 0 ||
                    versionsLoading
                  }
                  aria-label={`Bulk rendering for ${selectedCatalogLabel ?? 'form type'}`}
                >
                  <MoreHorizontal className='h-4 w-4' aria-hidden />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align='start'
                className='w-[min(100vw-2rem,22rem)]'
              >
                <DropdownMenuItem
                  disabled={
                    countBespokeBulkTargets(
                      templates,
                      'pause',
                      pinsForCurrentFormType
                    ) === 0
                  }
                  onClick={() =>
                    onOpenBulk(
                      'pause',
                      `All versions of ${selectedCatalogLabel ?? String(selectedRogerDocFormType)} listed below (${pinsForCurrentFormType.length} schema version${pinsForCurrentFormType.length === 1 ? '' : 's'}).`,
                      pinsForCurrentFormType
                    )
                  }
                >
                  <Pause className='h-3.5 w-3.5 mr-2' aria-hidden />
                  Pause all Live for this form type
                </DropdownMenuItem>
                <DropdownMenuItem
                  disabled={
                    countBespokeBulkTargets(
                      templates,
                      'resume',
                      pinsForCurrentFormType
                    ) === 0
                  }
                  onClick={() =>
                    onOpenBulk(
                      'resume',
                      `All versions of ${selectedCatalogLabel ?? String(selectedRogerDocFormType)} listed below (${pinsForCurrentFormType.length} schema version${pinsForCurrentFormType.length === 1 ? '' : 's'}).`,
                      pinsForCurrentFormType
                    )
                  }
                >
                  <Play className='h-3.5 w-3.5 mr-2' aria-hidden />
                  Resume all Paused for this form type
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
        {actions}
      </div>

      <div className='rounded-md border'>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className='py-1.5'>Name</TableHead>
              <TableHead className='py-1.5'>Effective From</TableHead>
              <TableHead className='py-1.5'>Effective Until</TableHead>
              <TableHead className='py-1.5 text-right'>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {versionsLoading ? (
              <TableRow>
                <TableCell colSpan={4} className='text-center py-8'>
                  <Loader2 className='h-5 w-5 animate-spin mx-auto' />
                </TableCell>
              </TableRow>
            ) : versionsError ? (
              <TableRow>
                <TableCell
                  colSpan={4}
                  className='text-center py-8 text-destructive'
                >
                  Could not load schema versions for this form type.
                  <span className='mt-1 block text-sm text-muted-foreground'>
                    {versionsError}
                  </span>
                </TableCell>
              </TableRow>
            ) : versions.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={4}
                  className='text-center py-8 text-muted-foreground'
                >
                  No schema versions found for this visit type.
                </TableCell>
              </TableRow>
            ) : (
              versions.map(v => {
                const active = isEffective(v);
                const versionKey = `${v.name}::${v.version ?? ''}`;
                const selectedKey = selectedVersion
                  ? `${selectedVersion.name}::${selectedVersion.version ?? ''}`
                  : null;
                const selected = selectedKey === versionKey;
                const versionBulkPin = selectedRogerDocFormType
                  ? pinFromFormSchemaVersion(
                      selectedRogerDocFormType as RogerDocFormType,
                      v
                    )
                  : null;
                const versionBulkPins = versionBulkPin ? [versionBulkPin] : [];
                return (
                  <TableRow
                    key={versionKey}
                    className={`cursor-pointer ${selected ? 'bg-muted' : 'hover:bg-muted/50'}`}
                    onClick={() => onVersionSelect(v)}
                  >
                    <TableCell className='font-medium py-1.5'>
                      {v.name}
                      {active && (
                        <Badge className='ml-2 text-xs' variant='default'>
                          Active
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className='py-1.5'>
                      {v.effective_from
                        ? formatDateForDisplay(v.effective_from) || '—'
                        : '—'}
                    </TableCell>
                    <TableCell className='py-1.5'>
                      {v.effective_until
                        ? formatDateForDisplay(v.effective_until) || '—'
                        : '—'}
                    </TableCell>
                    <TableCell className='text-right py-1.5'>
                      <div
                        className='flex justify-end'
                        onClick={event => event.stopPropagation()}
                      >
                        <DropdownMenu modal={false}>
                          <DropdownMenuTrigger asChild>
                            <Button
                              type='button'
                              variant='ghost'
                              size='sm'
                              className='h-8 w-8 p-0'
                              aria-label='Schema version actions'
                              onClick={() => {
                                if (!selected) onVersionSelect(v);
                              }}
                            >
                              <MoreHorizontal className='h-4 w-4' />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align='end'>
                            <DropdownMenuItem
                              disabled={
                                countBespokeBulkTargets(
                                  templates,
                                  'pause',
                                  versionBulkPins
                                ) === 0
                              }
                              onClick={() =>
                                onOpenBulk(
                                  'pause',
                                  `Schema version "${v.name}" (${v.version ?? 'unknown version'}).`,
                                  versionBulkPins
                                )
                              }
                            >
                              <Pause className='h-3.5 w-3.5 mr-2' />
                              Pause Live for this version
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              disabled={
                                countBespokeBulkTargets(
                                  templates,
                                  'resume',
                                  versionBulkPins
                                ) === 0
                              }
                              onClick={() =>
                                onOpenBulk(
                                  'resume',
                                  `Schema version "${v.name}" (${v.version ?? 'unknown version'}).`,
                                  versionBulkPins
                                )
                              }
                            >
                              <Play className='h-3.5 w-3.5 mr-2' />
                              Resume Paused for this version
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>
    </>
  );
}
